import {PartyType} from '@sollapay/enums';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {IdentityTypeToggle} from '@/components/identity';
import {
  RosterPane,
  RosterPaneHeader,
  RosterSidebarLayout,
  useRosterSelection
} from '@/components/roster';
import {AiExtractionStatus, getScopeAi} from '@/features/ai-extraction';
import {
  useBuyerSidebarItems,
  useSelectFirstInvalidBuyer,
  useSyncBuyersWithAiFields
} from '@/features/buyers/hooks';
import {parseBuyerFieldKey} from '@/features/buyers/utils';
import {createEmptyBuyerFormEntry} from '@/features/buyers/validation';

import {BuyerCorporateFields} from './BuyerCorporateFields';
import {BuyerPersonFields} from './BuyerPersonFields';
import {BuyerTypeToggle} from './BuyerTypeToggle';

import type {RosterSidebarItem, RosterSidebarLayoutProps} from '@/components/roster';
import type {AiSuggestion} from '@/features/ai-extraction';
import type {BuyerRowAi} from '@/features/buyers/hooks';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {FC} from 'react';
import type {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayReplace,
  UseFormReturn
} from 'react-hook-form';

export interface BuyerDetailsSectionProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  rowAi: BuyerRowAi;
  borders?: RosterSidebarLayoutProps['borders'];
  markFieldModified: (field: string) => void;
  reindexBuyerAiFields: (removedIndex: number) => void;
  buyerFields: FieldArrayWithId<CreateBuyerFormValues, 'buyers'>[];
  appendBuyer: UseFieldArrayAppend<CreateBuyerFormValues, 'buyers'>;
  removeBuyer: (index: number) => void;
  replaceBuyers: UseFieldArrayReplace<CreateBuyerFormValues, 'buyers'>;
}

export const BuyerDetailsSection: FC<BuyerDetailsSectionProps> = ({
  form,
  aiFields,
  isProcessing,
  rowAi,
  borders,
  markFieldModified,
  reindexBuyerAiFields,
  buyerFields: fields,
  appendBuyer: append,
  removeBuyer: remove,
  replaceBuyers: replace
}) => {
  const {t} = useTranslation('trustAccounts');
  const prefix = 'view.buyers.addBuyerModal.buyerDetails';
  const {selectedIndex, select, selectAfterAdd, adjustAfterRemove} = useRosterSelection(
    fields.length
  );
  const items = useBuyerSidebarItems(form, fields, rowAi);

  useSyncBuyersWithAiFields({form, aiFields, fieldsLength: fields.length, append, replace});
  useSelectFirstInvalidBuyer(form, select);

  const handleAdd = () => {
    selectAfterAdd();
    append(createEmptyBuyerFormEntry(), {shouldFocus: false});
  };

  const handleRemove = (index: number) => {
    reindexBuyerAiFields(index);
    remove(index);
    adjustAfterRemove(index);
  };

  return (
    <RosterSidebarLayout
      borders={borders}
      title={t(`${prefix}.sidebarTitle`)}
      items={items}
      selectedIndex={selectedIndex}
      onSelect={select}
      onAdd={handleAdd}
      addLabel={t(`${prefix}.addBuyer`)}
      summary={fields.length > 0 ? t(`${prefix}.buyersCount`, {count: fields.length}) : undefined}
      loading={
        isProcessing
          ? {
              sidebarLabel: t(`${prefix}.extracting.sidebarLabel`),
              title: t(`${prefix}.extracting.title`),
              subtitle: t(`${prefix}.extracting.subtitle`)
            }
          : undefined
      }
      emptyState={{
        title: t(`${prefix}.empty.title`),
        subtitle: t(`${prefix}.empty.subtitle`)
      }}
    >
      {items.map((item, index) => (
        // Index in the key remounts shifted buyers: nested useFieldArray names can't change in place.
        <RosterPane key={`${item.id}-${index}`} active={index === selectedIndex}>
          <BuyerRow
            form={form}
            index={index}
            item={item}
            totalBuyers={fields.length}
            aiFields={aiFields}
            isProcessing={isProcessing}
            rowAi={rowAi}
            markFieldModified={markFieldModified}
            onRemove={() => handleRemove(index)}
          />
        </RosterPane>
      ))}
    </RosterSidebarLayout>
  );
};

interface BuyerRowProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  index: number;
  item: RosterSidebarItem;
  totalBuyers: number;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  rowAi: BuyerRowAi;
  markFieldModified: (field: string) => void;
  onRemove: () => void;
}

const BuyerRow: FC<BuyerRowProps> = ({
  form,
  index,
  item,
  totalBuyers,
  aiFields,
  isProcessing,
  rowAi,
  markFieldModified,
  onRemove
}) => {
  const {t} = useTranslation('trustAccounts');
  const prefix = 'view.buyers.addBuyerModal.buyerDetails';

  const partyType = useWatch({control: form.control, name: `buyers.${index}.partyType`});
  const isCorporate = partyType === PartyType.ORGANIZATION;

  const handleTypeChange = (next: PartyType) => {
    form.setValue(`buyers.${index}.partyType`, next, {shouldDirty: true});
    markFieldModified(`buyers.${index}.partyType`);
    const leftBranchPrefixes = isCorporate ? ['organization.', 'signatories.'] : ['person.'];
    for (const key of aiFields.keys()) {
      const field = parseBuyerFieldKey(key);
      if (field?.index === index && leftBranchPrefixes.some(p => field.path.startsWith(p))) {
        markFieldModified(key);
      }
    }
  };

  const buyerAi = getScopeAi(rowAi, form.getValues(`buyers.${index}.person.buyerKey`));
  const isGenerating = isProcessing || buyerAi.isProcessing;

  const uploadStatus = (
    <AiExtractionStatus
      isProcessing={buyerAi.isProcessing}
      suggestionsCount={buyerAi.suggestionsCount}
      onFillManually={buyerAi.stop}
    />
  );

  return (
    <>
      <RosterPaneHeader
        title={item.title}
        typeLabel={item.subtitle}
        status={item.status}
        positionLabel={t(`${prefix}.buyerPosition`, {current: index + 1, total: totalBuyers})}
        removeLabel={t(`${prefix}.removeBuyer`)}
        onRemove={onRemove}
      />

      <div className="grid grid-cols-2 justify-items-start gap-6">
        <BuyerTypeToggle value={partyType ?? PartyType.PERSON} onChange={handleTypeChange} />
        {!isCorporate && (
          <Controller
            name={`buyers.${index}.person.idType`}
            control={form.control}
            render={({field}) => (
              <IdentityTypeToggle value={field.value} onChange={field.onChange} />
            )}
          />
        )}
      </div>

      {isCorporate ? (
        <BuyerCorporateFields
          form={form}
          index={index}
          aiFields={aiFields}
          isProcessing={isGenerating}
          markFieldModified={markFieldModified}
          uploadStatus={uploadStatus}
        />
      ) : (
        <BuyerPersonFields
          form={form}
          index={index}
          aiFields={aiFields}
          isProcessing={isGenerating}
          markFieldModified={markFieldModified}
          uploadStatus={uploadStatus}
        />
      )}
    </>
  );
};
